import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faGift, faBolt, faFire } from '@fortawesome/free-solid-svg-icons';
import API_CONFIG from '../../apiConfig';

interface BannerData {
    id: string;
    title: string;
    message: string;
    sub_message: string;
    discount: string;
    cta_text: string;
    cta_link: string;
    timer_duration: number;
    variant: 'default' | 'urgent' | 'gift';
    is_active: boolean;
    start_date: string | null;
    end_date: string | null;
    created_at: string;
    updated_at: string;
}

interface TopPromoBannerProps {
    autoShow?: boolean;
    showDelay?: number;
}

const TopPromoBanner: React.FC<TopPromoBannerProps> = ({
    autoShow = true,
    showDelay = 1000
}) => {
    const [bannerData, setBannerData] = useState<BannerData | null>(null);
    const [loading, setLoading] = useState(true);
    const [isVisible, setIsVisible] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [, setError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [apiFailed, setApiFailed] = useState(false);
    const [noActiveOffers, setNoActiveOffers] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {

        // Fetch banner data from API
        const fetchBannerData = async (isRetry = false) => {
            try {
                setError(null);
                const url = API_CONFIG.PROMO_BANNER_URL;

                // Add timeout to prevent hanging requests
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000);

                const response = await fetch(url, {
                    signal: controller.signal,
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                clearTimeout(timeoutId);

                const result = await response.json();

                if (result.success) {
                    if (result.data) {
                        // Valid banner data received
                        setBannerData(result.data);

                        // Calculate remaining time based on end_date
                        if (result.data.end_date) {
                            const endTime = new Date(result.data.end_date).getTime();
                            const currentTime = new Date().getTime();
                            const remainingSeconds = Math.max(0, Math.floor((endTime - currentTime) / 1000));
                            setTimeLeft(remainingSeconds);
                        } else {
                            // Fallback to timer_duration if no end_date
                            setTimeLeft(result.data.timer_duration * 60);
                        }

                        setRetryCount(0); // Reset retry count on success
                        setApiFailed(false); // Reset API failed state on success
                        setNoActiveOffers(false); // Reset no active offers state
                        setIsRetrying(false); // Reset retry state on success

                        // Clear retry timeout on success
                        if (retryTimeoutRef.current) {
                            clearTimeout(retryTimeoutRef.current);
                            retryTimeoutRef.current = null;
                        }

                        if (autoShow) {
                            setTimeout(() => setIsVisible(true), showDelay);
                        } else {
                            setIsVisible(true);
                        }
                    } else {
                        // API successful but no active offers - hide banner and stop API calls
                        setBannerData(null);
                        setIsVisible(false);
                        setApiFailed(false); // Not a failure, just no offers
                        setNoActiveOffers(true); // Mark that there are no active offers
                        setRetryCount(0);
                        setIsRetrying(false); // Reset retry state

                        // Clear retry timeout
                        if (retryTimeoutRef.current) {
                            clearTimeout(retryTimeoutRef.current);
                            retryTimeoutRef.current = null;
                        }
                    }
                } else {
                    throw new Error(`API returned success=false. Response: ${JSON.stringify(result)}`);
                }
            } catch (error) {
                console.error('Top Banner API error:', error);

                // Handle specific error types
                let errorMessage = 'Unknown error occurred';
                if (error instanceof Error) {
                    if (error.name === 'AbortError') {
                        errorMessage = 'Request timeout - banner service is slow';
                    } else if (error.message.includes('Failed to fetch')) {
                        errorMessage = 'Network error - unable to reach banner service';
                    } else {
                        errorMessage = error.message;
                    }
                }

                setError(errorMessage);

                // Retry logic with 15-second intervals
                if (retryCount < 2) {
                    console.log(`Retrying top banner fetch... (attempt ${retryCount + 1}/3) - next retry in 15 seconds`);
                    setRetryCount(prev => prev + 1);
                    setIsRetrying(true);

                    // Clear any existing retry timeout
                    if (retryTimeoutRef.current) {
                        clearTimeout(retryTimeoutRef.current);
                    }

                    // Retry after 15 seconds
                    const timeout = setTimeout(() => fetchBannerData(true), 15000);
                    retryTimeoutRef.current = timeout;
                    return;
                }

                console.error('Top Banner API failed after all retries - hiding banner to avoid showing outdated offers');

                // Don't show banner when API fails - set no data
                setBannerData(null);
                setIsVisible(false);
                setApiFailed(true);

                // Clear retry timeout on final failure
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                    retryTimeoutRef.current = null;
                }
            } finally {
                setLoading(false);
            }
        };

        // Fetch on component mount
        fetchBannerData();

        // Auto-refresh every 10 minutes to get updates (only if API hasn't failed and there might be offers)
        const refreshInterval = setInterval(() => {
            // Only refresh if API hasn't failed completely, we don't know there are no offers, and we're not retrying
            if (!apiFailed && !noActiveOffers && !isRetrying) {
                fetchBannerData();
            }
        }, 10 * 60 * 1000);

        const handleVisibilityChange = () => {
            if (!document.hidden && !apiFailed && !noActiveOffers && !isRetrying) {
                fetchBannerData();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(refreshInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);

            // Clear retry timeout on cleanup
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, [autoShow, showDelay, retryCount, apiFailed, noActiveOffers, isRetrying]);

    // Timer countdown effect
    useEffect(() => {
        if (!isVisible || !bannerData) return;

        const interval = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    // Timer reached 0, hide the banner
                    setIsVisible(false);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isVisible, bannerData]);

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        }
        return `${minutes}m ${secs}s`;
    };

    const handleClose = () => {
        setIsVisible(false);
        setIsDismissed(true);
    };

    const handleCtaClick = () => {
        if (bannerData?.cta_link) {
            window.location.href = bannerData.cta_link;
        }
    };

    // Don't render if loading, no banner data, not visible, or dismissed
    if (loading || !isVisible || !bannerData || isDismissed) {
        return null;
    }

    const getVariantStyles = () => {
        switch (bannerData.variant) {
            case 'urgent':
                return {
                    background: 'bg-gradient-to-r from-red-500 to-orange-500',
                    icon: faFire,
                    iconColor: 'text-yellow-200',
                    badgeColor: 'bg-yellow-400 text-red-900',
                    textColor: 'text-white',
                    buttonTextColor: 'text-red-700'
                };
            case 'gift':
                return {
                    background: 'bg-gradient-to-r from-purple-500 to-pink-500',
                    icon: faGift,
                    iconColor: 'text-yellow-200',
                    badgeColor: 'bg-yellow-400 text-purple-900',
                    textColor: 'text-white',
                    buttonTextColor: 'text-purple-700'
                };
            default:
                return {
                    background: 'bg-gradient-to-r from-purple-600 to-purple-700',
                    icon: faBolt,
                    iconColor: 'text-yellow-200',
                    badgeColor: 'bg-yellow-400 text-purple-900',
                    textColor: 'text-white',
                    buttonTextColor: 'text-purple-700'
                };
        }
    };

    const styles = getVariantStyles();

    return (
        <div className={`top-promo-banner w-full ${styles.background} ${styles.textColor} relative animate-slideDown`}>
            {/* Mobile Layout - Always vertical for small screens */}
            <div className="block sm:hidden px-3 py-2 relative">
                <div className="flex flex-col items-center space-y-2 text-center">
                    {/* Title and Discount - Allow text to wrap */}
                    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                        <FontAwesomeIcon icon={styles.icon} className={`${styles.iconColor} text-sm flex-shrink-0`} />
                        <span className="text-sm font-bold uppercase text-center break-words">
                            {bannerData.title}: {bannerData.discount}
                        </span>
                    </div>

                    {/* Timer and CTA */}
                    <div className="flex items-center space-x-3">
                        <span className="text-sm font-bold">
                            {formatTime(timeLeft)}
                        </span>
                        <button
                            onClick={handleCtaClick}
                            className={`bg-white ${styles.buttonTextColor} hover:bg-accent-sand px-2 py-1 rounded text-xs font-bold transition-all duration-200 whitespace-nowrap shadow-sm`}
                        >
                            {bannerData.cta_text}
                        </button>
                    </div>
                </div>

                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute top-1.5 right-3 text-white hover:text-gray-200 p-1 transition-colors duration-200"
                    aria-label="Close banner"
                >
                    <FontAwesomeIcon icon={faTimes} className="text-sm" />
                </button>
            </div>

            {/* Dynamic Layout for medium and larger screens */}
            <div className="hidden sm:block px-3 py-2 relative">
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
                    {/* Icon and Title/Discount - Can wrap to next line if too long */}
                    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 min-w-0">
                        <FontAwesomeIcon icon={styles.icon} className={`${styles.iconColor} text-sm flex-shrink-0`} />
                        <span className="text-sm font-bold uppercase break-words min-w-0">
                            {bannerData.title}: {bannerData.discount}
                        </span>
                    </div>

                    {/* Sub-message - Hidden on smaller screens, wraps on larger */}
                    <div className="hidden md:block min-w-0">
                        <span className="text-sm font-medium break-words">
                            {bannerData.sub_message}
                        </span>
                    </div>

                    {/* Timer - Flexible */}
                    <div className="flex items-center space-x-1 flex-shrink-0">
                        <span className="text-sm font-bold whitespace-nowrap">
                            {formatTime(timeLeft)}
                        </span>
                    </div>

                    {/* CTA Button - Never wraps */}
                    <div className="flex-shrink-0">
                        <button
                            onClick={handleCtaClick}
                            className={`bg-white ${styles.buttonTextColor} hover:bg-accent-sand px-3 py-1.5 rounded-md font-bold text-sm transition-all duration-200 whitespace-nowrap shadow-sm`}
                        >
                            {bannerData.cta_text}
                        </button>
                    </div>

                    {/* Close button - Always visible */}
                    <div className="flex-shrink-0">
                        <button
                            onClick={handleClose}
                            className="text-white hover:text-gray-200 p-1 transition-colors duration-200"
                            aria-label="Close banner"
                        >
                            <FontAwesomeIcon icon={faTimes} className="text-sm" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TopPromoBanner;